// calendarDataService.js - Servicio completo para extraer y formatear datos médicos para el calendario

import { getDetailTreatmentsByPatient } from './detailTreatmentService';
import diagnosticImagingService from './diagnosticImagingService';
import LaboratoryOrdersService from './laboratoryOrdersService';
import medicalRecordsService from './medicalRecordService';
import molecularMedicineService from './molecularMedicineService';
import NoteService from './noteService';
import nursingTasksService from './nursingTaskService';

/**
 * Servicio principal para obtener todos los datos médicos y formatearlos para el calendario
 */
class CalendarDataService {
  
  // ========================================================================
  // 💊 TRATAMIENTOS DETALLADOS
  // ========================================================================
  async getTreatmentDetailsData(patientId) {
    try {
      console.log('🔍 Loading treatment details for patient:', patientId);
      
      const response = await getDetailTreatmentsByPatient(patientId);
      console.log('📋 Raw treatment response:', response);
      
      let treatmentsData = [];
      
      // Normalizar los datos dependiendo de la estructura de respuesta
      if (response.data) {
        treatmentsData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        treatmentsData = response;
      } else if (response.treatments) {
        treatmentsData = Array.isArray(response.treatments) ? response.treatments : [response.treatments];
      }

      const events = [];
      
      treatmentsData.forEach(treatment => {
        const baseEvent = {
          type: 'treatmentDetails',
          originalData: treatment,
          title: treatment.treatment_info?.name || treatment.name || 'Unnamed Treatment',
          description: treatment.treatment_info?.protocol_code || treatment.protocol_code || '-',
          patientId: patientId,
          treatmentId: treatment.treatment_id || treatment._id || treatment.id,
          status: treatment.treatment_info?.status || treatment.status || 'active',
          doctorName: treatment.doctor_info?.doctor_name || 'Not assigned',
          consentStatus: treatment.consent_status?.is_signed ? 'Signed' : 'Pending'
        };

        // Extraer múltiples fechas del tratamiento
        this.extractTreatmentDates(treatment, baseEvent, events);
      });

      console.log('✅ Treatment events processed:', events.length);
      return events;

    } catch (error) {
      console.error('❌ Error loading treatment details:', error);
      return [];
    }
  }

  // ========================================================================
  // 🖼️ DIAGNÓSTICO POR IMAGEN
  // ========================================================================
  async getDiagnosticImagingData(patientId) {
    try {
      console.log('🔍 Loading diagnostic imaging for patient:', patientId);
      
      const response = await diagnosticImagingService.getDiagnosticsByPatient(patientId);
      console.log('🖼️ Raw diagnostic response:', response);
      
      let studiesData = [];
      
      if (response.success && response.data) {
        studiesData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (!response.success) {
        console.error('Failed to load diagnostic studies:', response.message);
        return [];
      }

      const events = [];
      
      studiesData.forEach(study => {
        const baseEvent = {
          type: 'diagnosticImaging',
          originalData: study,
          title: study.name || study.studyName || 'Diagnostic Study',
          description: `${study.type || study.studyType || 'Study'} - ${study.bodyPart || 'Unspecified'}`,
          patientId: patientId,
          studyId: study._id || study.id,
          studyType: study.type || study.studyType || 'Radiografía',
          bodyPart: study.bodyPart || '',
          status: study.status || 'In Progress',
          priority: study.priority || 'Medium',
          doctorId: study.doctorId || '',
          consentStatus: study.consent ? 'Obtained' : 'Pending'
        };

        // Extraer fechas del estudio
        this.extractDiagnosticDates(study, baseEvent, events);
      });

      console.log('✅ Diagnostic events processed:', events.length);
      return events;

    } catch (error) {
      console.error('❌ Error loading diagnostic imaging:', error);
      return [];
    }
  }

  // ========================================================================
  // 🧪 ÓRDENES DE LABORATORIO
  // ========================================================================
  async getLaboratoryOrdersData(patientId) {
    try {
      console.log('🔍 Loading laboratory orders for patient:', patientId);
      
      const response = await LaboratoryOrdersService.getPatientOrders(patientId);
      console.log('🧪 Raw laboratory response:', response);
      
      let ordersData = [];
      
      if (response.success && response.data) {
        ordersData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        ordersData = response;
      } else if (!response.success) {
        console.error('Failed to load laboratory orders:', response.message);
        return [];
      }

      const events = [];
      
      ordersData.forEach(order => {
        const baseEvent = {
          type: 'laboratory',
          originalData: order,
          title: order.testName || order.name || 'Laboratory Test',
          description: `${order.testType || 'Lab Test'} - ${order.priority || 'Normal'}`,
          patientId: patientId,
          orderId: order._id || order.id,
          testType: order.testType || '',
          status: order.status || 'Pending',
          priority: order.priority || 'Normal',
          doctorId: order.doctorId || order.requestedBy || '',
          resultStatus: order.resultStatus || 'Pending'
        };

        // Extraer fechas de la orden de laboratorio
        this.extractLaboratoryDates(order, baseEvent, events);
      });

      console.log('✅ Laboratory events processed:', events.length);
      return events;

    } catch (error) {
      console.error('❌ Error loading laboratory orders:', error);
      return [];
    }
  }

  // ========================================================================
  // 🏥 REGISTROS MÉDICOS
  // ========================================================================
  async getMedicalRecordsData(patientId) {
    try {
      console.log('🔍 Loading medical records for patient:', patientId);
      
      const response = await medicalRecordsService.getRecordsByPatient(patientId);
      console.log('🏥 Raw medical records response:', response);
      
      let recordsData = [];
      
      if (response.success && response.data) {
        recordsData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        recordsData = response;
      } else if (!response.success) {
        console.error('Failed to load medical records:', response.message);
        return [];
      }

      const events = [];
      
      recordsData.forEach(record => {
        const baseEvent = {
          type: 'medicalRecord',
          originalData: record,
          title: record.title || record.name || 'Medical Record',
          description: `${record.category || 'Record'} - ${record.priority || 'Normal'}`,
          patientId: patientId,
          recordId: record._id || record.id,
          category: record.category || '',
          status: record.status || 'Active',
          priority: record.priority || 'Normal',
          doctorId: record.doctorId || record.createdBy || '',
          department: record.department || ''
        };

        // Extraer fechas del registro médico
        this.extractMedicalRecordDates(record, baseEvent, events);
      });

      console.log('✅ Medical records events processed:', events.length);
      return events;

    } catch (error) {
      console.error('❌ Error loading medical records:', error);
      return [];
    }
  }

  // ========================================================================
  // 🧬 MEDICINA MOLECULAR
  // ========================================================================
  async getMolecularMedicineData(patientId) {
    try {
      console.log('🔍 Loading molecular medicine studies for patient:', patientId);
      
      const response = await molecularMedicineService.getStudiesByPatient(patientId);
      console.log('🧬 Raw molecular medicine response:', response);
      
      let studiesData = [];
      
      if (response.success && response.data) {
        studiesData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        studiesData = response;
      } else if (!response.success) {
        console.error('Failed to load molecular medicine studies:', response.message);
        return [];
      }

      const events = [];
      
      studiesData.forEach(study => {
        const baseEvent = {
          type: 'molecularMedicine',
          originalData: study,
          title: study.name || study.studyName || 'Molecular Study',
          description: `${study.studyType || 'Genetic Study'} - ${study.clinicalArea || 'Genetics'}`,
          patientId: patientId,
          studyId: study._id || study.id,
          studyType: study.studyType || study.type || '',
          status: study.status || 'In Progress',
          priority: study.priority || 'Medium',
          clinicalArea: study.clinicalArea || '',
          geneTargets: study.geneTargets || []
        };

        // Extraer fechas del estudio de medicina molecular
        this.extractMolecularMedicineDates(study, baseEvent, events);
      });

      console.log('✅ Molecular medicine events processed:', events.length);
      return events;

    } catch (error) {
      console.error('❌ Error loading molecular medicine studies:', error);
      return [];
    }
  }

  // ========================================================================
  // 📝 NOTAS MÉDICAS
  // ========================================================================
  // Fixed methods for calendarDataService.js

// Fix para el método getGeneralNotesData en calendarDataService.js

async getGeneralNotesData(patientId) {
  try {
    console.log('🔍 Loading general notes for patient:', patientId);
    
    const response = await NoteService.getNotesByPatient(patientId, {
      status: 'active'
    });
    console.log('📝 Raw general notes response:', response);
    
    let notesData = [];
    
    // Manejo robusto de la respuesta del servicio
    if (response && response.success && response.data) {
      notesData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (response && response.data && !response.success) {
      // Algunos servicios retornan data sin success flag
      notesData = Array.isArray(response.data) ? response.data : [response.data];
    } else if (Array.isArray(response)) {
      notesData = response;
    } else if (response && response.notes) {
      notesData = Array.isArray(response.notes) ? response.notes : [response.notes];
    } else if (response && !response.success) {
      console.error('Failed to load general notes:', response.message);
      return [];
    } else {
      // Fallback: asumir que response es la data directamente
      notesData = Array.isArray(response) ? response : (response ? [response] : []);
    }

    console.log('📝 Processed notes data:', notesData.length, 'notes found');

    const events = [];
    
    notesData.forEach((note, index) => {
      console.log(`📝 Processing note ${index + 1}:`, {
        id: note._id || note.id,
        title: note.title,
        createdAt: note.createdAt,
        hasCreatedAt: !!note.createdAt
      });

      const baseEvent = {
        type: 'generalNotes',
        originalData: note,
        title: note.title || 'Medical Note',
        description: note.details ? 
          (note.details.length > 100 ? note.details.substring(0, 100) + '...' : note.details) : 
          'Medical Note',
        patientId: patientId,
        noteId: note._id || note.id,
        
        priority: note.priority || 'medium',
        status: note.status || 'active',
        isPrivate: note.isPrivate || false,
        tags: note.tags || [],
        
        authorId: note.createdBy?._id || note.createdBy || '',
        authorName: note.createdBy?.name || note.createdBy?.username || 'Unknown Author',
        
        isArchived: note.status === 'archived',
        noteContent: note.details
      };

      // SIMPLIFICADO: Solo crear un evento principal con la fecha de creación
      if (note.createdAt) {
        const mainEvent = {
          ...baseEvent,
          id: `general-note-${baseEvent.noteId}-main`,
          date: new Date(note.createdAt),
          dateType: 'note_date',
          eventSubtype: 'General Medical Note',
          doctor: baseEvent.authorName // Para mostrar en el calendario
        };
        
        console.log('📝 Adding general note event:', mainEvent.id, mainEvent.date);
        events.push(mainEvent);
      } else {
        console.warn('⚠️ Note without createdAt date:', note._id);
        
        // Fallback con fecha actual si no hay createdAt
        const fallbackEvent = {
          ...baseEvent,
          id: `general-note-${baseEvent.noteId}-fallback`,
          date: new Date(), // Usar fecha actual como fallback
          dateType: 'note_date',
          eventSubtype: 'General Medical Note',
          doctor: baseEvent.authorName,
          title: baseEvent.title + ' (No Date)'
        };
        
        console.log('📝 Adding fallback general note event:', fallbackEvent.id);
        events.push(fallbackEvent);
      }
    });

    console.log('✅ General notes events processed:', events.length);
    return events;

  } catch (error) {
    console.error('❌ Error loading general notes:', error);
    return [];
  }
}


  // ========================================================================
  // 💉 TAREAS DE ENFERMERÍA
  // ========================================================================
  async getNursingTasksData(patientId) {
    try {
      console.log('🔍 Loading nursing tasks for patient:', patientId);
      
      const response = await nursingTasksService.getTasksByPatient(patientId);
      console.log('💉 Raw nursing tasks response:', response);
      
      let tasksData = [];
      
      if (response.success && response.data) {
        tasksData = Array.isArray(response.data) ? response.data : [response.data];
      } else if (Array.isArray(response)) {
        tasksData = response;
      } else if (!response.success) {
        console.error('Failed to load nursing tasks:', response.message);
        return [];
      }

      const events = [];
      
      tasksData.forEach(task => {
        const baseEvent = {
          type: 'nursing',
          originalData: task,
          title: task.title || task.name || task.taskName || 'Nursing Task',
          description: `${task.taskType || 'Care Task'} - ${task.priority || 'Normal'}`,
          patientId: patientId,
          taskId: task._id || task.id,
          taskType: task.taskType || task.type || '',
          status: task.status || 'Pending',
          priority: task.priority || 'Normal',
          assignedTo: task.assignedTo || '',
          assignedBy: task.assignedBy || task.createdBy || '',
          frequency: task.frequency || 'Once'
        };

        // Extraer fechas de las tareas de enfermería
        this.extractNursingTasksDates(task, baseEvent, events);
      });

      console.log('✅ Nursing tasks events processed:', events.length);
      return events;

    } catch (error) {
      console.error('❌ Error loading nursing tasks:', error);
      return [];
    }
  }

  // ========================================================================
  // 🔄 MÉTODO PRINCIPAL - COMBINAR TODOS LOS DATOS
  // ========================================================================
  async getAllMedicalEvents(patientId) {
    try {
      console.log('🚀 Loading all medical events for patient:', patientId);
      
      // Ejecutar todas las consultas en paralelo para mejor rendimiento
      const [
        treatmentEvents,
        diagnosticEvents,
        laboratoryEvents,
        medicalRecordEvents,
        molecularMedicineEvents,
        generalNotesEvents,
        nursingEvents
      ] = await Promise.all([
        this.getTreatmentDetailsData(patientId),
        this.getDiagnosticImagingData(patientId),
        this.getLaboratoryOrdersData(patientId),
        this.getMedicalRecordsData(patientId),
        this.getMolecularMedicineData(patientId),
        this.getGeneralNotesData(patientId),
        this.getNursingTasksData(patientId)
      ]);

      // Combinar todos los eventos
      const allEvents = [
        ...treatmentEvents,
        ...diagnosticEvents,
        ...laboratoryEvents,
        ...medicalRecordEvents,
        ...molecularMedicineEvents,
        ...generalNotesEvents,
        ...nursingEvents
      ];

      // Ordenar eventos por fecha (más recientes primero)
      allEvents.sort((a, b) => new Date(b.date) - new Date(a.date));

      // Agregar metadatos útiles
      const eventsWithMetadata = allEvents.map((event, index) => ({
        ...event,
        globalId: `${event.type}-${event.id || index}`,
        timestamp: event.date.getTime(),
        dayOfYear: this.getDayOfYear(event.date),
        color: this.getEventColor(event.type),
        bgColor: this.getEventBgColor(event.type),
        icon: this.getEventIcon(event.type),
        typeLabel: this.getEventTypeLabel(event.type)
      }));

      console.log(`✅ Total medical events loaded: ${eventsWithMetadata.length}`);
      console.log('📊 Events by type:', this.getEventsSummary(eventsWithMetadata));
      
      return eventsWithMetadata;

    } catch (error) {
      console.error('❌ Error loading all medical events:', error);
      return [];
    }
  }

  // ========================================================================
  // 🛠️ MÉTODOS AUXILIARES PARA EXTRAER FECHAS
  // ========================================================================

  extractTreatmentDates(treatment, baseEvent, events) {
    // Fecha de inicio del tratamiento
    if (treatment.treatment_info?.start_date || treatment.start_date) {
      events.push({
        ...baseEvent,
        id: `treatment-${baseEvent.treatmentId}-start`,
        date: new Date(treatment.treatment_info?.start_date || treatment.start_date),
        dateType: 'start_date',
        eventSubtype: 'Treatment Start',
        title: `${baseEvent.title} - Start`,
        priority: 'high'
      });
    }

    // Fecha estimada de finalización
    if (treatment.treatment_info?.estimated_end_date || treatment.estimated_end_date) {
      events.push({
        ...baseEvent,
        id: `treatment-${baseEvent.treatmentId}-end`,
        date: new Date(treatment.treatment_info?.estimated_end_date || treatment.estimated_end_date),
        dateType: 'estimated_end_date',
        eventSubtype: 'Treatment End (Est.)',
        title: `${baseEvent.title} - End (Estimated)`,
        priority: 'medium'
      });
    }

    // Fecha de creación y actualización
    this.extractCommonDates(treatment, baseEvent, events, 'treatment');

    // Fecha de firma de consentimiento
    if (treatment.consent_status?.signed_date) {
      events.push({
        ...baseEvent,
        id: `treatment-${baseEvent.treatmentId}-consent`,
        date: new Date(treatment.consent_status.signed_date),
        dateType: 'consent_signed',
        eventSubtype: 'Consent Signed',
        title: `${baseEvent.title} - Consent Signed`,
        priority: 'medium'
      });
    }

    // Fechas de las fases del tratamiento
    if (treatment.phases_summary && Array.isArray(treatment.phases_summary)) {
      treatment.phases_summary.forEach((phase, index) => {
        if (phase.start_date) {
          events.push({
            ...baseEvent,
            id: `treatment-${baseEvent.treatmentId}-phase-${index}-start`,
            date: new Date(phase.start_date),
            dateType: 'phase_start',
            eventSubtype: `Phase ${index + 1} Start`,
            title: `${baseEvent.title} - Phase ${index + 1}`,
            description: phase.name || `Phase ${index + 1}`,
            priority: 'medium'
          });
        }
      });
    }
  }

  extractDiagnosticDates(study, baseEvent, events) {
    // Fecha programada
    if (study.scheduledDate) {
      events.push({
        ...baseEvent,
        id: `diagnostic-${baseEvent.studyId}-scheduled`,
        date: new Date(study.scheduledDate),
        dateType: 'scheduled_date',
        eventSubtype: 'Diagnostic Scheduled',
        title: `${baseEvent.title} - Scheduled`,
        priority: 'high'
      });
    }

    // Fecha de finalización
    if (study.completedDate) {
      events.push({
        ...baseEvent,
        id: `diagnostic-${baseEvent.studyId}-completed`,
        date: new Date(study.completedDate),
        dateType: 'completed_date',
        eventSubtype: 'Diagnostic Completed',
        title: `${baseEvent.title} - Completed`,
        priority: 'medium'
      });
    }

    // Fechas comunes (creación, actualización)
    this.extractCommonDates(study, baseEvent, events, 'diagnostic');
  }

  extractLaboratoryDates(order, baseEvent, events) {
    // Fecha de solicitud
    if (order.requestedDate || order.orderDate) {
      events.push({
        ...baseEvent,
        id: `lab-${baseEvent.orderId}-requested`,
        date: new Date(order.requestedDate || order.orderDate),
        dateType: 'requested_date',
        eventSubtype: 'Lab Order Requested',
        title: `${baseEvent.title} - Ordered`,
        priority: 'high'
      });
    }

    // Fecha programada para toma de muestra
    if (order.scheduledDate || order.collectionDate) {
      events.push({
        ...baseEvent,
        id: `lab-${baseEvent.orderId}-scheduled`,
        date: new Date(order.scheduledDate || order.collectionDate),
        dateType: 'collection_date',
        eventSubtype: 'Sample Collection',
        title: `${baseEvent.title} - Collection`,
        priority: 'high'
      });
    }

    // Fecha de resultados
    if (order.resultDate || order.completedDate) {
      events.push({
        ...baseEvent,
        id: `lab-${baseEvent.orderId}-result`,
        date: new Date(order.resultDate || order.completedDate),
        dateType: 'result_date',
        eventSubtype: 'Lab Results Available',
        title: `${baseEvent.title} - Results`,
        priority: 'medium'
      });
    }

    // Fechas comunes
    this.extractCommonDates(order, baseEvent, events, 'lab');
  }

  extractMedicalRecordDates(record, baseEvent, events) {
    // Fecha del evento médico
    if (record.eventDate || record.recordDate) {
      events.push({
        ...baseEvent,
        id: `record-${baseEvent.recordId}-event`,
        date: new Date(record.eventDate || record.recordDate),
        dateType: 'event_date',
        eventSubtype: 'Medical Event',
        title: `${baseEvent.title} - Event`,
        priority: 'medium'
      });
    }

    // Fecha de revisión
    if (record.reviewDate) {
      events.push({
        ...baseEvent,
        id: `record-${baseEvent.recordId}-review`,
        date: new Date(record.reviewDate),
        dateType: 'review_date',
        eventSubtype: 'Record Review',
        title: `${baseEvent.title} - Review`,
        priority: 'low'
      });
    }

    // Fechas comunes
    this.extractCommonDates(record, baseEvent, events, 'record');
  }

  extractMolecularMedicineDates(study, baseEvent, events) {
    // Fecha de solicitud del estudio
    if (study.requestedDate) {
      events.push({
        ...baseEvent,
        id: `molecular-${baseEvent.studyId}-requested`,
        date: new Date(study.requestedDate),
        dateType: 'requested_date',
        eventSubtype: 'Molecular Study Requested',
        title: `${baseEvent.title} - Requested`,
        priority: 'high'
      });
    }

    // Fecha de inicio del análisis
    if (study.analysisStartDate) {
      events.push({
        ...baseEvent,
        id: `molecular-${baseEvent.studyId}-analysis`,
        date: new Date(study.analysisStartDate),
        dateType: 'analysis_start',
        eventSubtype: 'Analysis Started',
        title: `${baseEvent.title} - Analysis`,
        priority: 'medium'
      });
    }

    // Fecha de finalización
    if (study.completedDate || study.reportDate) {
      events.push({
        ...baseEvent,
        id: `molecular-${baseEvent.studyId}-completed`,
        date: new Date(study.completedDate || study.reportDate),
        dateType: 'completed_date',
        eventSubtype: 'Study Completed',
        title: `${baseEvent.title} - Completed`,
        priority: 'medium'
      });
    }

    // Fechas comunes
    this.extractCommonDates(study, baseEvent, events, 'molecular');
  }

  extractNotesDates(note, baseEvent, events) {
    // Fecha de la consulta/evento
    if (note.eventDate || note.visitDate) {
      events.push({
        ...baseEvent,
        id: `note-${baseEvent.noteId}-event`,
        date: new Date(note.eventDate || note.visitDate),
        dateType: 'event_date',
        eventSubtype: 'Medical Note',
        title: `${baseEvent.title} - Note`,
        priority: 'medium'
      });
    }

    // Fecha de archivo
    if (note.archivedDate && note.archived) {
      events.push({
        ...baseEvent,
        id: `note-${baseEvent.noteId}-archived`,
        date: new Date(note.archivedDate),
        dateType: 'archived_date',
        eventSubtype: 'Note Archived',
        title: `${baseEvent.title} - Archived`,
        priority: 'low'
      });
    }

    // Fechas comunes
    this.extractCommonDates(note, baseEvent, events, 'note');
  }

  extractNursingTasksDates(task, baseEvent, events) {
    // Fecha programada de la tarea
    if (task.scheduledDate || task.dueDate) {
      events.push({
        ...baseEvent,
        id: `nursing-${baseEvent.taskId}-scheduled`,
        date: new Date(task.scheduledDate || task.dueDate),
        dateType: 'scheduled_date',
        eventSubtype: 'Nursing Task Scheduled',
        title: `${baseEvent.title} - Due`,
        priority: this.getNursingPriority(task.priority, task.status)
      });
    }

    // Fecha de inicio de la tarea
    if (task.startedDate) {
      events.push({
        ...baseEvent,
        id: `nursing-${baseEvent.taskId}-started`,
        date: new Date(task.startedDate),
        dateType: 'started_date',
        eventSubtype: 'Task Started',
        title: `${baseEvent.title} - Started`,
        priority: 'medium'
      });
    }

    // Fecha de finalización
    if (task.completedDate) {
      events.push({
        ...baseEvent,
        id: `nursing-${baseEvent.taskId}-completed`,
        date: new Date(task.completedDate),
        dateType: 'completed_date',
        eventSubtype: 'Task Completed',
        title: `${baseEvent.title} - Completed`,
        priority: 'low'
      });
    }

    // Fechas comunes
    this.extractCommonDates(task, baseEvent, events, 'nursing');
  }

  extractCommonDates(item, baseEvent, events, prefix) {
    const itemId = baseEvent.treatmentId || baseEvent.studyId || baseEvent.orderId || 
                   baseEvent.recordId || baseEvent.noteId || baseEvent.taskId;

    // Fecha de creación
    if (item.createdAt || item.created_date) {
      events.push({
        ...baseEvent,
        id: `${prefix}-${itemId}-created`,
        date: new Date(item.createdAt || item.created_date),
        dateType: 'created_date',
        eventSubtype: 'Created',
        title: `${baseEvent.title} - Created`,
        priority: 'low'
      });
    }

    // Última actualización
    if (item.updatedAt || item.last_updated || item.lastUpdate) {
      events.push({
        ...baseEvent,
        id: `${prefix}-${itemId}-updated`,
        date: new Date(item.updatedAt || item.last_updated || item.lastUpdate),
        dateType: 'last_updated',
        eventSubtype: 'Updated',
        title: `${baseEvent.title} - Updated`,
        priority: 'low'
      });
    }
  }

  // ========================================================================
  // 🎨 FUNCIONES DE UTILIDAD PARA EL CALENDARIO
  // ========================================================================
  
  getDayOfYear(date) {
    const start = new Date(date.getFullYear(), 0, 0);
    const diff = date - start;
    return Math.floor(diff / (1000 * 60 * 60 * 24));
  }

  getEventColor(type) {
  const colors = {
    treatmentDetails: '#0d6efd',
    diagnosticImaging: '#fd7e14',
    laboratory: '#dc3545',
    generalNotes: '#198754',  // ← Cambio: 'notes' → 'generalNotes'
    medicalRecord: '#6f42c1',
    nursing: '#20c997',
    molecularMedicine: '#6610f2'
  };
  return colors[type] || '#6c757d';
}

getEventBgColor(type) {
  const bgColors = {
    treatmentDetails: '#e7f1ff',
    diagnosticImaging: '#fff3e0',
    laboratory: '#f8d7da',
    generalNotes: '#d1e7dd',  // ← Cambio: 'notes' → 'generalNotes'
    medicalRecord: '#e2d9f3',
    nursing: '#c3e6cb',
    molecularMedicine: '#e0cffc'
  };
  return bgColors[type] || '#f8f9fa';
}

getEventIcon(type) {
  const icons = {
    treatmentDetails: 'FiActivity',
    diagnosticImaging: 'FiEye',
    laboratory: 'FiFileText',
    generalNotes: 'FiEdit3',  // ← Cambio: 'notes' → 'generalNotes'
    medicalRecord: 'FiUser',
    nursing: 'FiHeart',
    molecularMedicine: 'FiActivity'
  };
  return icons[type] || 'FiCircle';
}

getEventTypeLabel(type) {
  const labels = {
    treatmentDetails: 'Treatment Details',
    diagnosticImaging: 'Diagnostic Imaging',
    laboratory: 'Laboratory',
    generalNotes: 'General Notes',  // ← Cambio: 'notes' → 'generalNotes'
    medicalRecord: 'Medical Record',
    nursing: 'Nursing Tasks',
    molecularMedicine: 'Molecular Medicine'
  };
  return labels[type] || 'Medical Event';
}

  getNursingPriority(priority, status) {
    if (status === 'overdue') return 'high';
    if (priority === 'urgent' || priority === 'high') return 'high';
    if (priority === 'medium' || priority === 'normal') return 'medium';
    return 'low';
  }

  getEventsSummary(events) {
    const summary = {};
    events.forEach(event => {
      summary[event.type] = (summary[event.type] || 0) + 1;
    });
    return summary;
  }

  // ========================================================================
  // 📊 MÉTODOS ADICIONALES DE UTILIDAD
  // ========================================================================

  /**
   * Filtra eventos por rango de fechas
   */
  filterEventsByDateRange(events, startDate, endDate) {
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= startDate && eventDate <= endDate;
    });
  }

  /**
   * Filtra eventos por tipo
   */
  filterEventsByType(events, types) {
    const typeArray = Array.isArray(types) ? types : [types];
    return events.filter(event => typeArray.includes(event.type));
  }

  /**
   * Agrupa eventos por fecha
   */
  groupEventsByDate(events) {
    const grouped = {};
    events.forEach(event => {
      const dateKey = event.date.toISOString().split('T')[0];
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(event);
    });
    return grouped;
  }

  /**
   * Obtiene eventos para un día específico
   */
  getEventsForDate(events, date) {
    const targetDate = new Date(date).toISOString().split('T')[0];
    return events.filter(event => {
      const eventDate = event.date.toISOString().split('T')[0];
      return eventDate === targetDate;
    });
  }

  /**
   * Obtiene próximos eventos (dentro de los próximos N días)
   */
  getUpcomingEvents(events, days = 7) {
    const now = new Date();
    const futureDate = new Date();
    futureDate.setDate(now.getDate() + days);
    
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate >= now && eventDate <= futureDate;
    }).sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  /**
   * Obtiene eventos vencidos
   */
  getOverdueEvents(events) {
    const now = new Date();
    return events.filter(event => {
      const eventDate = new Date(event.date);
      return eventDate < now && 
             (event.status === 'pending' || event.status === 'scheduled') &&
             event.dateType !== 'created_date' && 
             event.dateType !== 'last_updated';
    });
  }

  /**
   * Calcula estadísticas de eventos
   */
  getEventsStatistics(events) {
    const now = new Date();
    const stats = {
      total: events.length,
      byType: this.getEventsSummary(events),
      byStatus: {},
      byPriority: {},
      upcoming: 0,
      overdue: 0,
      completed: 0
    };

    events.forEach(event => {
      // Por estado
      stats.byStatus[event.status] = (stats.byStatus[event.status] || 0) + 1;
      
      // Por prioridad
      stats.byPriority[event.priority] = (stats.byPriority[event.priority] || 0) + 1;
      
      // Próximos eventos (próximos 30 días)
      const eventDate = new Date(event.date);
      const thirtyDaysFromNow = new Date();
      thirtyDaysFromNow.setDate(now.getDate() + 30);
      
      if (eventDate >= now && eventDate <= thirtyDaysFromNow) {
        stats.upcoming++;
      }
      
      // Eventos vencidos
      if (eventDate < now && 
          (event.status === 'pending' || event.status === 'scheduled') &&
          event.dateType !== 'created_date' && 
          event.dateType !== 'last_updated') {
        stats.overdue++;
      }
      
      // Eventos completados
      if (event.status === 'completed' || event.status === 'finished') {
        stats.completed++;
      }
    });

    return stats;
  }
}

export default new CalendarDataService();